/**
 * Sprint T-18 · Notification Studio · Playground · orchestrator tab.
 *
 * Owns local state and coordinates left/right panels, action bar, and history.
 * Zero business logic; all rendering / validation / persistence lives in
 * `/api/notifications/studio/*` (T-18 backend).
 *
 * Backend endpoints used:
 *   POST /api/notifications/studio/render-preview      · live preview
 *   POST /api/notifications/studio/playground-history  · save entry
 *   POST /api/notifications/test-send                  · dry run / test send
 *   GET  /api/notifications/studio/playground-history  · history list
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import {
  useTemplates,
  useRenderPreview,
  useSavePlaygroundEntry,
  useTestSend,
} from "../../hooks/useNotifications";
import PlaygroundLeftPanel from "./shared/PlaygroundLeftPanel";
import PlaygroundRightPanel from "./shared/PlaygroundRightPanel";
import PlaygroundActionBar from "./shared/PlaygroundActionBar";
import PlaygroundHistoryList from "./shared/PlaygroundHistoryList";
import { notificationsApi } from "../../services/notificationsApi";

export default function PlaygroundTab() {
  const { user } = useAuth();

  // form state
  const [templateId, setTemplateId] = useState("");
  const [locale, setLocale] = useState("es-ES");
  const [tenantId, setTenantId] = useState(null);
  const [contextRaw, setContextRaw] = useState("{}");
  const [contextParsed, setContextParsed] = useState({});
  const [contextError, setContextError] = useState(null);

  // preview state
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [lastOutcome, setLastOutcome] = useState(null);

  // hooks
  const templatesQ = useTemplates();
  const renderM = useRenderPreview();
  const saveM = useSavePlaygroundEntry();
  const testSendM = useTestSend();

  const templates = templatesQ.data?.items || [];
  const selectedTpl = templates.find((t) => t.template_id === templateId);
  const requiredContext = selectedTpl?.required_context || [];

  // Auto-fill defaults when template changes (server-driven preview_context)
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await notificationsApi.getTemplate(templateId);
        if (cancelled) return;
        const pc = r.data?.template?.preview_context || {};
        const formatted = JSON.stringify(pc, null, 2);
        setContextRaw(formatted);
        setContextParsed(pc);
        setContextError(null);
        setPreview(null);
        setPreviewError(null);
      } catch (e) {
        console.warn("failed to fetch template defaults", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // Auto-render preview when context changes (debounced)
  useEffect(() => {
    if (!templateId || contextError) return;
    const timer = setTimeout(() => {
      renderM.mutate(
        {
          template_id: templateId,
          context: contextParsed || {},
          tenant_id: tenantId || null,
          locale,
        },
        {
          onSuccess: (data) => {
            setPreview(data);
            setPreviewError(null);
          },
          onError: (err) => {
            setPreview(null);
            setPreviewError(err?.response?.data?.detail || err?.message || "preview failed");
          },
        },
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [templateId, JSON.stringify(contextParsed), tenantId, locale, contextError]);

  const intentJson = useMemo(() => {
    if (!templateId) return null;
    return {
      template_id: templateId,
      channel: selectedTpl?.channel || "email",
      topic: selectedTpl?.topic,
      locale,
      tenant_id: tenantId,
      to: user?.email || "(superadmin)",
      priority: "normal",
      context: contextParsed || {},
    };
  }, [templateId, selectedTpl, locale, tenantId, contextParsed, user]);

  const handleContextChange = ({ raw, parsed, error }) => {
    setContextRaw(raw);
    setContextParsed(parsed);
    setContextError(error);
  };

  const handleFillDefaults = async () => {
    if (!templateId) return;
    try {
      const r = await notificationsApi.getTemplate(templateId);
      const pc = r.data?.template?.preview_context || {};
      const formatted = JSON.stringify(pc, null, 2);
      setContextRaw(formatted);
      setContextParsed(pc);
      setContextError(null);
      toast.success("Contexto restaurado a los defaults del template");
    } catch (e) {
      toast.error("No se pudieron cargar los defaults");
    }
  };

  const runManualPreview = () => {
    if (!templateId) return;
    if (contextError) {
      toast.error("Corrige el JSON antes de renderizar");
      return;
    }
    renderM.mutate(
      { template_id: templateId, context: contextParsed || {}, tenant_id: tenantId || null, locale },
      {
        onSuccess: (data) => {
          setPreview(data);
          setPreviewError(null);
          toast.success("Preview actualizado");
        },
        onError: (err) => {
          setPreview(null);
          setPreviewError(err?.response?.data?.detail || err?.message || "preview failed");
          toast.error("Preview falló");
        },
      },
    );
  };

  const handleDryRun = async () => {
    if (!templateId) return;
    try {
      const entry = await saveM.mutateAsync({
        template_id: templateId,
        context_json: contextParsed || {},
        tenant_id: tenantId || null,
        locale,
        action: "dry_run",
        outcome: preview ? "sent" : "unknown",
      });
      setLastOutcome(entry?.entry?.outcome || "sent");
      toast.success("Dry Run guardado en historial · no se envió email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry Run falló");
    }
  };

  const handleTestSend = async (toEmail) => {
    if (!templateId) return;
    if (!toEmail) {
      toast.error("Sin email de superadmin");
      return;
    }
    try {
      // Q2 = a · Test Send ONLY to the logged-in superadmin email.
      const res = await testSendM.mutateAsync({
        channel: selectedTpl?.channel || "email",
        to: toEmail,
        template_id: templateId,
        context: contextParsed || {},
        tenant_id: tenantId || null,
        locale,
        topic: selectedTpl?.topic,
      });
      setLastOutcome(res?.outcome || "sent");
      // Save history entry linked to the delivery
      await saveM.mutateAsync({
        template_id: templateId,
        context_json: contextParsed || {},
        tenant_id: tenantId || null,
        locale,
        action: "test_send",
        outcome: res?.outcome || "sent",
        delivery_id: res?.delivery?.id || null,
      });
      const outcome = res?.outcome || "sent";
      if (outcome === "sent") {
        toast.success(`Test Send · ${outcome} → ${toEmail}`);
      } else {
        toast.warning(`Test Send terminó como ${outcome}: ${res?.reason || ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test Send falló");
    }
  };

  const handleSaveHistory = async () => {
    if (!templateId) return;
    try {
      await saveM.mutateAsync({
        template_id: templateId,
        context_json: contextParsed || {},
        tenant_id: tenantId || null,
        locale,
        action: "save",
        outcome: preview ? "sent" : "unknown",
      });
      toast.success("Entry guardado");
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const handleRestore = (entry) => {
    setTemplateId(entry.template_id);
    setLocale(entry.locale || "es-ES");
    setTenantId(entry.tenant_id || null);
    const formatted = JSON.stringify(entry.context_json || {}, null, 2);
    setContextRaw(formatted);
    setContextParsed(entry.context_json || {});
    setContextError(null);
    toast.info(`Restaurado desde historial · ${entry.template_id}`);
  };

  return (
    <div className="space-y-4" data-testid="studio-playground-tab">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PlaygroundLeftPanel
          templates={templates}
          templatesLoading={templatesQ.isLoading}
          templateId={templateId}
          onTemplateId={setTemplateId}
          locale={locale}
          onLocale={setLocale}
          tenantId={tenantId}
          onTenantId={setTenantId}
          contextRaw={contextRaw}
          onContextChange={handleContextChange}
          onFillDefaults={handleFillDefaults}
          onValidate={runManualPreview}
          missingContext={preview?.missing_context || []}
          requiredContext={requiredContext}
        />
        <PlaygroundRightPanel
          preview={preview}
          intentJson={intentJson}
          loading={renderM.isPending}
          error={previewError}
        />
      </div>

      <PlaygroundActionBar
        disabled={!templateId || Boolean(contextError)}
        currentUser={user}
        onDryRun={handleDryRun}
        onTestSend={handleTestSend}
        onSaveHistory={handleSaveHistory}
        isRendering={renderM.isPending}
        isTestSending={testSendM.isPending}
        isSaving={saveM.isPending}
        lastOutcome={lastOutcome}
      />

      <PlaygroundHistoryList onRestore={handleRestore} limit={20} />
    </div>
  );
}
